import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  MoveShiftsResponse,
  UpdatedActivityPart,
  UpdatedShiftPart,
} from '@wilson/api/gateway';
import {
  Shift,
  TimelineShift,
  UnassignedShift,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import {
  AddShiftWithoutActivitiesToCellViewUnassignedRegion,
  RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion,
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  ShiftTimelineUnassignedRegionState,
  TimelineUpsertActivityToUnassignedRegion,
  TimelineUpsertActivityV2,
  UpdateShiftWithoutActivitiesFromUnassignedRegion,
} from '@wilson/shift-timeline/state';
import { cloneDeep } from 'lodash';
import { TimelineAdjustTimeService } from '../../../services/timeline-adjust-time.service';
import { TimelineCommonOperationsService } from '../../../services/timeline-common-operations.service';

@Injectable({
  providedIn: 'root',
})
export class UpdateTimelineAfterMoveService {
  private readonly store = inject(Store);
  private readonly timelineAdjustTimeService = inject(
    TimelineAdjustTimeService,
  );
  private timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );

  public updateTimelineCellViewMode(response: MoveShiftsResponse): void {
    response.forEach(this.handleCellViewMove.bind(this));
  }

  private handleCellViewMove(updatedShiftPart: UpdatedShiftPart): void {
    // Since the user can assign/unassigned the shift while the drawer is open, we have to find it again.
    const clonedAssignedShift = cloneDeep(
      this.store.selectSnapshot(
        ShiftTimelineDataState.getShiftWithoutActivities(updatedShiftPart.id),
      ),
    );

    const clonedUnassignedShift = cloneDeep(
      this.timelineCommonOperationsService.getShiftFromUnassignedRegion(
        updatedShiftPart.id,
      ),
    );

    if (clonedAssignedShift) {
      const updatedShift = this.updateShiftWithNewPropertiesInPlace(
        updatedShiftPart,
        clonedAssignedShift,
      );

      this.timelineAdjustTimeService.adjustV2TimelineShiftTimeAndUpdateValidations(
        updatedShift,
      );
    } else if (clonedUnassignedShift) {
      const updatedUnassignedShift =
        this.updateUnassignedShiftWithNewPropertiesInPlace(
          updatedShiftPart,
          clonedUnassignedShift,
        );

      // A shift can exist in multiple stacks so we need to remove first before adding again
      this.store.dispatch([
        new RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion({
          shiftId: updatedUnassignedShift.id,
        }),
        new AddShiftWithoutActivitiesToCellViewUnassignedRegion(
          updatedUnassignedShift,
        ),
      ]);
    } else {
      console.error(
        `Unable to update timeline because no matching shift with id ${updatedShiftPart.id} found in neither unassigned nor assigned shifts state.`,
      );
    }
  }

  public updateTimelineStandardViewMode(response: MoveShiftsResponse): void {
    const areActivitiesVisible = this.store.selectSnapshot(
      ShiftTimelineSettingsState.areActivitiesVisible,
    );
    response.forEach((updatedShiftPart) => {
      this.handleStandardViewMove(updatedShiftPart, areActivitiesVisible);
    });
  }

  private handleStandardViewMove(
    updatedShiftPart: UpdatedShiftPart,
    areActivitiesVisible: boolean,
  ): void {
    // Since the user can assign/unassigned the shift while the drawer is open, we have to find it again.
    const clonedAssignedShift = cloneDeep(
      this.store.selectSnapshot(
        ShiftTimelineDataState.getShiftWithoutActivities(updatedShiftPart.id),
      ),
    );

    const clonedUnassignedShift = cloneDeep(
      this.timelineCommonOperationsService.getShiftFromUnassignedRegion(
        updatedShiftPart.id,
      ),
    );

    if (clonedAssignedShift) {
      if (areActivitiesVisible) {
        this.updateAssignedActivitiesInState(
          updatedShiftPart.activities,
          clonedAssignedShift,
        );
      }
      const updatedShift = this.updateShiftWithNewPropertiesInPlace(
        updatedShiftPart,
        clonedAssignedShift,
      );

      this.timelineAdjustTimeService.adjustV2TimelineShiftTimeAndUpdateValidations(
        updatedShift,
      );
    } else if (clonedUnassignedShift) {
      if (areActivitiesVisible) {
        this.updateUnAssignedActivitiesInState(
          updatedShiftPart.activities,
          clonedUnassignedShift.id,
        );
      }
      const updatedUnassignedShift =
        this.updateUnassignedShiftWithNewPropertiesInPlace(
          updatedShiftPart,
          clonedUnassignedShift,
        );

      this.store.dispatch([
        new UpdateShiftWithoutActivitiesFromUnassignedRegion(
          updatedUnassignedShift,
        ),
      ]);
    } else {
      console.error(
        `Unable to update timeline because no matching shift with id ${updatedShiftPart.id} found in neither unassigned nor assigned shifts state.`,
      );
    }
  }

  private updateUnAssignedActivitiesInState(
    updatedActivityParts: UpdatedActivityPart[],
    shiftId: string,
  ): void {
    const shiftActivities = cloneDeep(
      this.store.selectSnapshot(
        ShiftTimelineUnassignedRegionState.unassignedShiftActivitiesArray(
          shiftId,
        ),
      ),
    );

    shiftActivities.forEach((activity) => {
      const matchingUpdatedActivityPart = updatedActivityParts.find(
        (data) => data.id === activity.id,
      );
      if (matchingUpdatedActivityPart) {
        activity.startDatetime = matchingUpdatedActivityPart.startDatetime;
        activity.endDatetime = matchingUpdatedActivityPart.endDatetime;
      }

      this.store.dispatch([
        new TimelineUpsertActivityToUnassignedRegion(activity, shiftId),
      ]);
    });
  }

  private updateAssignedActivitiesInState(
    updatedActivityParts: UpdatedActivityPart[],
    shift: Shift &
      WithPreparedAttributes & {
        id: string;
      },
  ): void {
    const shiftActivities = cloneDeep(
      this.store.selectSnapshot(
        ShiftTimelineDataState.shiftActivitiesArray(shift.id as string),
      ),
    );

    shiftActivities.forEach((activity) => {
      const matchingUpdatedActivityPart = updatedActivityParts.find(
        (data) => data.id === activity.id,
      );
      if (matchingUpdatedActivityPart) {
        activity.startDatetime = matchingUpdatedActivityPart.startDatetime;
        activity.endDatetime = matchingUpdatedActivityPart.endDatetime;
      }

      this.store.dispatch(
        new TimelineUpsertActivityV2([activity], shift.id as string),
      );
    });
  }

  private updateShiftWithNewPropertiesInPlace(
    updatedShiftParts: UpdatedShiftPart,
    shift: TimelineShift,
  ): TimelineShift {
    shift.startDate = updatedShiftParts.startDate;
    shift.startDatetime = updatedShiftParts.startDatetime;
    shift.endDatetime = updatedShiftParts.endDatetime;
    shift.publicationStatus = updatedShiftParts.publicationStatus;

    return shift;
  }

  private updateUnassignedShiftWithNewPropertiesInPlace(
    updatedShiftParts: UpdatedShiftPart,
    shift: UnassignedShift,
  ): UnassignedShift {
    shift.startDatetime = updatedShiftParts.startDatetime;
    shift.endDatetime = updatedShiftParts.endDatetime;
    return shift;
  }
}
